import { mediaRef } from '@/services/mediaAssets';
/* Achievement images stay next to their metadata so webpack can bundle them. */
/* eslint-disable global-require */

/**
 * @typedef {'experience' | 'destination' | 'theme' | 'collection'} TagCategory
 */

/**
 * @typedef {'Corporate Retreat' | 'Conference' | 'Adventure' | 'Holiday Party' | 'Annual Event' | 'Scouting Trip'} TripType
 */

/**
 * @typedef {Object} TripTag
 * @property {string} slug
 * @property {string} label
 * @property {TagCategory} category
 */

/**
 * @typedef {Object} TripAttendee
 * @property {string} employeeSlug
 * @property {string} displayName
 * @property {string} role
 */

/**
 * @typedef {'Adventure' | 'Food & Drink' | 'Conference' | 'Culture' | 'Nature' | 'Family'} AchievementCategory
 */

/**
 * @typedef {Object} Achievement
 * @property {string} slug
 * @property {string} title
 * @property {string} [image]
 * @property {AchievementCategory} category
 * @property {string} tripSlug
 */

/**
 * @typedef {Object} FirstExperience
 * @property {string} slug
 * @property {string} title
 * @property {string} tripSlug
 * @property {string[]} [employeeSlugs]
 */

/**
 * @typedef {Object} ExperienceDNA
 * @property {number} [conference]
 * @property {number} [food]
 * @property {number} [adventure]
 * @property {number} [family]
 * @property {number} [nightlife]
 * @property {number} [nature]
 * @property {number} [culture]
 * @property {number} [relaxation]
 * @property {number} [luxury]
 */

/**
 * @typedef {Object} TripDestination
 * @property {string} [city]
 * @property {string} [region]
 * @property {string} country
 * @property {string} [continent]
 */

/**
 * @typedef {Object} Trip
 * @property {string} slug
 * @property {string} title
 * @property {string} [subtitle]
 * @property {TripDestination} destination
 * @property {string} dateLabel
 * @property {string} [startDate]
 * @property {string} [endDate]
 * @property {TripType} tripType
 * @property {string} heroImage
 * @property {string} summary
 * @property {TripAttendee[]} attendees
 * @property {TripTag[]} tags
 * @property {Achievement[]} achievements
 * @property {FirstExperience[]} [firsts]
 * @property {ExperienceDNA} [experienceDNA]
 * @property {boolean} [familyFriendly]
 * @property {string} [familyNote]
 */

export const createTripAttendee = ({ employeeSlug, displayName, role }) => ({
  employeeSlug,
  displayName,
  role,
});

export const tripAttendees = (...attendees) => attendees.map(createTripAttendee);

export const tripTagDefinitions = [
  {
    slug: 'conference',
    label: 'Conference',
    category: 'experience',
    description: 'Trips centered around conferences, learning, and industry events.',
    icon: 'mdi-microphone',
  },
  {
    slug: 'scouting-trip',
    label: 'Scouting Trip',
    category: 'experience',
    description: 'Smaller exploratory trips used to evaluate future Reckon Crew destinations.',
    icon: 'mdi-map-search-outline',
  },
  {
    slug: 'corporate-retreat',
    label: 'Corporate Retreat',
    category: 'experience',
    description: 'Company retreats designed around connection, planning, and shared adventure.',
    icon: 'mdi-account-group',
  },
  {
    slug: 'holiday-party',
    label: 'Holiday Party',
    category: 'collection',
    description: 'Celebrations that bring the crew together around food and tradition.',
    icon: 'mdi-party-popper',
  },
  {
    slug: 'annual-event',
    label: 'Annual Event',
    category: 'theme',
    description: 'Recurring annual experiences, traditions, and company gatherings.',
    icon: 'mdi-calendar-star',
  },
  {
    slug: 'adventure',
    label: 'Adventure',
    category: 'experience',
    description: 'Experience-led trips built around exploration.',
    icon: 'mdi-compass-outline',
  },
  {
    slug: 'family-friendly',
    label: 'Family-Friendly',
    category: 'experience',
    description: 'Experiences designed to include families and guests.',
    icon: 'mdi-human-male-female-child',
  },
  {
    slug: 'team-building',
    label: 'Team Building',
    category: 'experience',
    description: 'Experiences designed to strengthen relationships and trust.',
    icon: 'mdi-account-multiple-check',
  },
  {
    slug: 'ai',
    label: 'AI',
    category: 'experience',
    description: 'Artificial intelligence sessions, trends, and conversations.',
    icon: 'mdi-brain',
  },
  {
    slug: 'software-development',
    label: 'Software Development',
    category: 'experience',
    description: 'Developer-focused sessions, tooling, and software craft.',
    icon: 'mdi-code-braces',
  },
  {
    slug: 'film',
    label: 'Film',
    category: 'experience',
    description: 'Film premieres, screenings, and creative storytelling.',
    icon: 'mdi-movie-open-outline',
  },
  {
    slug: 'road-trip',
    label: 'Road Trip',
    category: 'experience',
    description: 'Road miles, scenic drives, and shared travel days.',
    icon: 'mdi-road-variant',
  },
  {
    slug: 'international',
    label: 'International',
    category: 'destination',
    description: 'Trips outside the United States.',
    icon: 'mdi-earth',
  },
  {
    slug: 'australia',
    label: 'Australia',
    category: 'destination',
    description: 'Trips and moments across Australia.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'sydney',
    label: 'Sydney',
    category: 'destination',
    description: 'Sydney, Australia experiences and city moments.',
    icon: 'mdi-city-variant-outline',
  },
  {
    slug: 'united-kingdom',
    label: 'United Kingdom',
    category: 'destination',
    description: 'Trips and moments across the United Kingdom.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'london',
    label: 'London',
    category: 'destination',
    description: 'London experiences, neighborhoods, and city moments.',
    icon: 'mdi-city-variant-outline',
  },
  {
    slug: 'shoreditch',
    label: 'Shoreditch',
    category: 'destination',
    description: 'Shoreditch neighborhood experiences, pubs, food, and creative energy.',
    icon: 'mdi-map-marker-radius-outline',
  },
  {
    slug: 'city-exploration',
    label: 'City Exploration',
    category: 'destination',
    description: 'Urban wandering, neighborhoods, landmarks, and local discovery.',
    icon: 'mdi-city-variant-outline',
  },
  {
    slug: 'urban',
    label: 'Urban',
    category: 'theme',
    description: 'City-centered experiences, neighborhoods, and urban energy.',
    icon: 'mdi-city',
  },
  {
    slug: 'fine-dining',
    label: 'Fine Dining',
    category: 'experience',
    description: 'Memorable tasting menus, celebrated restaurants, and special meals.',
    icon: 'mdi-silverware-fork-knife',
  },
  {
    slug: 'craft-beer',
    label: 'Craft Beer',
    category: 'experience',
    description: 'Breweries, tasting flights, and local beer culture.',
    icon: 'mdi-glass-mug-variant',
  },
  {
    slug: 'food',
    label: 'Food',
    category: 'experience',
    description: 'Meals, quick bites, and food-centered trip moments.',
    icon: 'mdi-food-fork-drink',
  },
  {
    slug: 'pubs',
    label: 'Pubs',
    category: 'experience',
    description: 'Pubs, bars, and casual local stops.',
    icon: 'mdi-glass-mug',
  },
  {
    slug: 'historic-pubs',
    label: 'Historic Pubs',
    category: 'experience',
    description: 'Classic pubs with history, character, and local atmosphere.',
    icon: 'mdi-glass-mug',
  },
  {
    slug: 'dim-sum',
    label: 'Dim Sum',
    category: 'experience',
    description: 'Shared baskets, Cantonese dishes, and first-time food experiences.',
    icon: 'mdi-food',
  },
  {
    slug: 'steakhouse',
    label: 'Steakhouse',
    category: 'experience',
    description: 'Steakhouse dinners and destination restaurants.',
    icon: 'mdi-food-steak',
  },
  {
    slug: 'live-music',
    label: 'Live Music',
    category: 'experience',
    description: 'Concerts, performances, and music-led moments.',
    icon: 'mdi-music',
  },
  {
    slug: 'ferry',
    label: 'Ferry',
    category: 'experience',
    description: 'Ferries and water crossings that become part of the story.',
    icon: 'mdi-ferry',
  },
  {
    slug: 'snowmobiling',
    label: 'Snowmobiling',
    category: 'experience',
    description: 'Snowmobile adventures across winter landscapes.',
    icon: 'mdi-snowmobile',
  },
  {
    slug: 'catamaran',
    label: 'Catamaran',
    category: 'experience',
    description: 'Catamaran sailing and water days.',
    icon: 'mdi-sail-boat',
  },
  {
    slug: 'hiking',
    label: 'Hiking',
    category: 'experience',
    description: 'Trail time, scenic walks, and active exploration.',
    icon: 'mdi-hiking',
  },
  {
    slug: 'scenic-railway',
    label: 'Scenic Railway',
    category: 'experience',
    description: 'Scenic railways and memorable transit experiences.',
    icon: 'mdi-train',
  },
  {
    slug: 'walking',
    label: 'Walking',
    category: 'experience',
    description: 'Walkable adventures, scenic routes, and exploring on foot.',
    icon: 'mdi-walk',
  },
  {
    slug: 'coastal-walk',
    label: 'Coastal Walk',
    category: 'experience',
    description: 'Walks along beaches, cliffs, harbors, and coastlines.',
    icon: 'mdi-hiking',
  },
  {
    slug: 'coastal',
    label: 'Coastal',
    category: 'theme',
    description: 'Coastline, harbor, beach, and ocean-led moments.',
    icon: 'mdi-waves',
  },
  {
    slug: 'crew-favorite',
    label: 'Crew Favorite',
    category: 'theme',
    description: 'Moments and places the crew especially loved.',
    icon: 'mdi-heart',
  },
  {
    slug: 'first-time',
    label: 'First Time',
    category: 'theme',
    description: 'Shared first-time experiences and new discoveries.',
    icon: 'mdi-star-four-points-outline',
  },
  {
    slug: 'hidden-gem',
    label: 'Hidden Gems',
    category: 'theme',
    description: 'Unexpected finds and tucked-away favorites.',
    icon: 'mdi-diamond-stone',
  },
  {
    slug: 'beach',
    label: 'Beach',
    category: 'experience',
    description: 'Beach days, island views, and ocean-side moments.',
    icon: 'mdi-beach',
  },
  {
    slug: 'mountains',
    label: 'Mountains',
    category: 'destination',
    description: 'Mountain views, highland drives, and alpine landscapes.',
    icon: 'mdi-image-filter-hdr',
  },
  {
    slug: 'glacier',
    label: 'Glacier',
    category: 'experience',
    description: 'Glaciers, ice fields, and winter landscapes.',
    icon: 'mdi-snowflake',
  },
  {
    slug: 'hot-springs',
    label: 'Hot Springs',
    category: 'experience',
    description: 'Geothermal lagoons, hot springs, and restorative water experiences.',
    icon: 'mdi-waves',
  },
  {
    slug: 'snorkeling',
    label: 'Snorkeling',
    category: 'experience',
    description: 'Underwater exploration and reef experiences.',
    icon: 'mdi-diving-snorkel',
  },
  {
    slug: 'sailing',
    label: 'Sailing',
    category: 'experience',
    description: 'Catamarans, boats, and time on the water.',
    icon: 'mdi-sail-boat',
  },
  {
    slug: 'cenotes',
    label: 'Cenotes',
    category: 'experience',
    description: 'Cenote swims and Riviera Maya exploration.',
    icon: 'mdi-water-circle',
  },
  {
    slug: 'resort',
    label: 'Resort',
    category: 'destination',
    description: 'Resort stays, amenities, and destination properties.',
    icon: 'mdi-palm-tree',
  },
  {
    slug: 'water-park',
    label: 'Water Park',
    category: 'experience',
    description: 'Water slides, pools, and aquatic attractions.',
    icon: 'mdi-pool',
  },
  {
    slug: 'super-jeep',
    label: 'Super Jeep',
    category: 'experience',
    description: 'Off-road exploration and rugged adventure travel.',
    icon: 'mdi-car-4wd',
  },
  {
    slug: 'brewery',
    label: 'Brewery',
    category: 'experience',
    description: 'Brewery stops and local taproom visits.',
    icon: 'mdi-glass-mug-variant',
  },
  {
    slug: 'museum',
    label: 'Museum',
    category: 'experience',
    description: 'Museum visits and exhibit-led experiences.',
    icon: 'mdi-bank',
  },
  {
    slug: 'wildlife',
    label: 'Wildlife',
    category: 'experience',
    description: 'Wildlife encounters and animal-centered experiences.',
    icon: 'mdi-bird',
  },
  {
    slug: 'nature',
    label: 'Nature',
    category: 'experience',
    description: 'Natural landscapes, outdoor views, and wild places.',
    icon: 'mdi-tree',
  },
  {
    slug: 'iceland',
    label: 'Iceland',
    category: 'destination',
    description: 'Trips and stories from Iceland.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'mexico',
    label: 'Mexico',
    category: 'destination',
    description: 'Trips and stories from Mexico.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'bahamas',
    label: 'Bahamas',
    category: 'destination',
    description: 'Trips and stories from the Bahamas.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'united-states',
    label: 'United States',
    category: 'destination',
    description: 'Trips and stories in the United States.',
    icon: 'mdi-map-marker',
  },
  {
    slug: 'bucket-list',
    label: 'Bucket List',
    category: 'theme',
    description: 'Experiences people remember as once-in-a-lifetime moments.',
    icon: 'mdi-format-list-checks',
  },
  {
    slug: 'family-tradition',
    label: 'Family Tradition',
    category: 'theme',
    description: 'Traditions shared with family and loved ones.',
    icon: 'mdi-home-heart',
  },
  {
    slug: 'luxury',
    label: 'Luxury',
    category: 'theme',
    description: 'Elevated, polished, and special occasion experiences.',
    icon: 'mdi-diamond-stone',
  },
  {
    slug: 'off-the-beaten-path',
    label: 'Off the Beaten Path',
    category: 'theme',
    description: 'Less expected routes, stops, and discoveries.',
    icon: 'mdi-sign-direction',
  },
  {
    slug: 'local-favorite',
    label: 'Local Favorite',
    category: 'theme',
    description: 'Places that feel known and loved locally.',
    icon: 'mdi-map-marker-heart',
  },
  {
    slug: 'iconic-landmark',
    label: 'Iconic Landmark',
    category: 'theme',
    description: 'Recognizable places that define a destination.',
    icon: 'mdi-image-filter-hdr',
  },
  {
    slug: 'unexpected-surprise',
    label: 'Unexpected Surprise',
    category: 'theme',
    description: 'Moments that became memorable because nobody saw them coming.',
    icon: 'mdi-star-shooting',
  },
  {
    slug: 'bermondsey-beer-mile',
    label: 'Bermondsey Beer Mile',
    category: 'theme',
    description: 'Brewery stops, taprooms, and walks along the Bermondsey Beer Mile.',
    icon: 'mdi-map-marker-path',
  },
  {
    slug: 'reckon-approved',
    label: 'Reckon Approved',
    category: 'theme',
    description: 'Scouted experiences that feel ready for a future Reckon Crew trip.',
    icon: 'mdi-check-decagram',
  },
  {
    slug: 'reckon-retreats',
    label: 'Reckon Retreats',
    category: 'collection',
    description: 'Corporate retreats and company getaways.',
    icon: 'mdi-account-group',
  },
  {
    slug: 'conference-series',
    label: 'Conference Series',
    category: 'collection',
    description: 'Conference trips and learning journeys.',
    icon: 'mdi-microphone',
  },
  {
    slug: 'australia-collection',
    label: 'Australia Collection',
    category: 'collection',
    description: 'Australia trips and experiences.',
    icon: 'mdi-earth',
  },
  {
    slug: 'iceland-collection',
    label: 'Iceland Collection',
    category: 'collection',
    description: 'Iceland trips and experiences.',
    icon: 'mdi-snowflake',
  },
  {
    slug: 'beer-adventures',
    label: 'Beer Adventures',
    category: 'collection',
    description: 'Breweries, pubs, and beer-led moments.',
    icon: 'mdi-glass-mug-variant',
  },
  {
    slug: 'best-restaurants',
    label: 'Best Restaurants',
    category: 'collection',
    description: 'Favorite restaurants and memorable meals.',
    icon: 'mdi-silverware-fork-knife',
  },
  {
    slug: 'water-adventures',
    label: 'Water Adventures',
    category: 'collection',
    description: 'Beaches, boats, ferries, snorkeling, and water days.',
    icon: 'mdi-waves',
  },
  {
    slug: 'great-walks',
    label: 'Great Walks',
    category: 'collection',
    description: 'Scenic walks, city wandering, and hiking experiences.',
    icon: 'mdi-walk',
  },
  {
    slug: 'family-adventures',
    label: 'Family Adventures',
    category: 'collection',
    description: 'Trips where loved ones were part of the adventure.',
    icon: 'mdi-human-male-female-child',
  },
];

export const tagRegistry = tripTagDefinitions.map(({ slug, label, category }) => ({
  slug,
  label,
  category,
}));

export const achievementDefinitions = [
  {
    slug: 'atvs-mojave-desert',
    title: 'ATVs in the Mojave Desert',
    category: 'Adventure',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/atvs-mojave-desert.png'),
  },
  {
    slug: 'utvs-mojave-desert',
    title: 'UTVs in the Mojave Desert',
    category: 'Adventure',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/utvs-mojave-desert.png'),
  },
  {
    slug: 'u2-at-sphere',
    title: 'U2 at Sphere',
    category: 'Culture',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/u2-at-sphere.png'),
  },
  {
    slug: 'valley-of-fire-off-roading',
    title: 'Valley of Fire Off-Roading',
    category: 'Adventure',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/valley-of-fire-off-roading.png'),
  },
  {
    slug: 'hiking-zion-national-park',
    title: 'Hiking Zion National Park',
    category: 'Nature',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/hiking-zion-national-park.png'),
  },
  {
    slug: 'eagles-at-sphere',
    title: 'Eagles at Sphere',
    category: 'Culture',
    tripSlug: 'aws-reinvent-las-vegas',
    image: mediaRef('adventures/merit-badges/eagles-at-sphere.png'),
  },
  {
    slug: 'ferry-to-manly-beach',
    title: 'Ferry to Manly Beach',
    category: 'Adventure',
    tripSlug: 'sxsw-sydney-2025',
    image: mediaRef('adventures/merit-badges/ferry-to-manly-beach.png'),
  },
  {
    slug: 'bondi-to-coogee-coastal-walk',
    title: 'Coogee to Bondi Coastal Walk',
    category: 'Adventure',
    tripSlug: 'sxsw-sydney-2025',
    image: mediaRef('adventures/merit-badges/bondi-to-coogee-coastal-walk.png'),
  },
  {
    slug: 'scenic-world-blue-mountains',
    title: 'Scenic World - Blue Mountains',
    category: 'Adventure',
    tripSlug: 'sxsw-sydney-2025',
    image: mediaRef('adventures/merit-badges/scenic-world-blue-mountains.png'),
  },
  {
    slug: 'snowmobiling-langjokull',
    title: 'Snowmobiling Langjokull',
    category: 'Adventure',
    tripSlug: 'iceland-corporate-retreat-2026',
    image: mediaRef('adventures/merit-badges/snowmobiling-langjokull.png'),
  },
  {
    slug: 'south-coast-to-reykjavik',
    title: 'South Coast to Reykjavik',
    category: 'Adventure',
    tripSlug: 'iceland-corporate-retreat-2026',
    image: mediaRef('adventures/merit-badges/south-coast-to-reykjavik.png'),
  },
  {
    slug: 'catamaran-sailing',
    title: 'Catamaran Sailing',
    category: 'Adventure',
    tripSlug: 'xcaret-2025',
    image: mediaRef('adventures/merit-badges/catamaran-sailing.png'),
  },
  {
    slug: 'coba-and-cenotes',
    title: 'Coba & Cenotes',
    category: 'Adventure',
    tripSlug: 'xcaret-2025',
    image: mediaRef('adventures/merit-badges/coba-and-cenotes.png'),
  },
  {
    slug: 'exploring-atlantis',
    title: 'Exploring Atlantis',
    category: 'Family',
    tripSlug: 'bahamas-2024',
    image: mediaRef('adventures/merit-badges/exploring-atlantis.png'),
  },
  {
    slug: 'bermondsey-beer-mile',
    title: 'Bermondsey Beer Mile',
    category: 'Food & Drink',
    tripSlug: 'sxsw-london-2025',
    image: mediaRef('adventures/merit-badges/bermondsey-beer-mile.png'),
  },
  {
    slug: 'historic-pub-crawl',
    title: 'Guinness Storehouse and Temple Bar',
    category: 'Food & Drink',
    tripSlug: 'sxsw-london-2025',
    image: mediaRef('adventures/merit-badges/historic-pub-crawl.png'),
  },
];

export const firstExperienceDefinitions = [
  {
    slug: 'first-trip-to-australia',
    title: 'First trip to Australia',
    tripSlug: 'sxsw-sydney-2025',
  },
  {
    slug: 'first-dim-sum',
    title: 'First dim sum experience',
    tripSlug: 'sxsw-sydney-2025',
    employeeSlugs: [
      'brandon-a',
      'brandon-l',
      'matt-g',
      'ben-b',
      'phillip-h',
    ],
  },
  {
    slug: 'first-coat-of-arms-pizza',
    title: 'First Coat of Arms Pizza',
    tripSlug: 'sxsw-sydney-2025',
  },
  {
    slug: 'first-glacier',
    title: 'First glacier',
    tripSlug: 'iceland-corporate-retreat-2026',
  },
  {
    slug: 'first-snowmobile',
    title: 'First snowmobile',
    tripSlug: 'iceland-corporate-retreat-2026',
  },
  {
    slug: 'first-geothermal-lagoon',
    title: 'First geothermal lagoon',
    tripSlug: 'iceland-corporate-retreat-2026',
  },
  {
    slug: 'first-cenote',
    title: 'First cenote',
    tripSlug: 'xcaret-2025',
  },
  {
    slug: 'first-catamaran',
    title: 'First catamaran sailing',
    tripSlug: 'xcaret-2025',
  },
  {
    slug: 'first-atlantis',
    title: 'First Atlantis adventure',
    tripSlug: 'bahamas-2024',
  },
];

const createLookup = (items) => items.reduce((lookup, item) => ({
  ...lookup,
  [item.slug]: item,
}), {});

const refsFromLookup = (lookup, slugs) => slugs
  .map((slug) => lookup[slug])
  .filter(Boolean);

const getComparableName = (item) => item.title || item.label || item.slug || '';

const toTripTag = ({ slug, label, category }) => ({
  slug,
  label,
  category,
});

export const tripTagsBySlug = createLookup(tripTagDefinitions);
export const achievementsBySlug = createLookup(achievementDefinitions);
export const firstExperiencesBySlug = createLookup(firstExperienceDefinitions);

export const tagRefs = (...slugs) => refsFromLookup(tripTagsBySlug, slugs).map(toTripTag);
export const achievementRefs = (...slugs) => refsFromLookup(achievementsBySlug, slugs);
export const firstRefs = (...slugs) => refsFromLookup(firstExperiencesBySlug, slugs);

export const getTripTagDefinition = (tagSlug) => tripTagsBySlug[tagSlug] || null;

export const getTripTagIcon = (tag) => {
  const definition = tag ? getTripTagDefinition(tag.slug) : null;

  return (definition && definition.icon) || 'mdi-tag-outline';
};

export const getTagCounts = (trips = []) => {
  const countMap = trips.reduce((counts, trip) => (
    (trip.tags || []).reduce((tripCounts, tag) => {
      const current = tripCounts[tag.slug] || { ...tag, count: 0 };
      return {
        ...tripCounts,
        [tag.slug]: { ...current, count: current.count + 1 },
      };
    }, counts)
  ), {});

  return Object.values(countMap).sort((a, b) => (
    b.count - a.count || getComparableName(a).localeCompare(getComparableName(b))
  ));
};

export const getFirstExperienceCounts = (trips = []) => {
  const countMap = trips.reduce((counts, trip) => (
    (trip.firsts || []).reduce((tripCounts, first) => {
      const current = tripCounts[first.slug] || { ...first, count: 0 };
      return {
        ...tripCounts,
        [first.slug]: { ...current, count: current.count + 1 },
      };
    }, counts)
  ), {});

  return Object.values(countMap).sort((a, b) => (
    b.count - a.count || getComparableName(a).localeCompare(getComparableName(b))
  ));
};

export const getTripsByTag = (trips, tagSlug) => (trips || []).filter((trip) => (
  (trip.tags || []).some((tag) => tag.slug === tagSlug)
));

export const getRelatedTrips = (currentTrip, allTrips) => {
  if (!currentTrip) {
    return [];
  }

  return (allTrips || [])
    .filter((trip) => trip.slug !== currentTrip.slug)
    .map((trip) => {
      const sharedTags = (trip.tags || []).filter((tag) => (
        (currentTrip.tags || []).some((currentTag) => currentTag.slug === tag.slug)
      ));

      return {
        trip,
        score: sharedTags.length,
        sharedTags,
      };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.trip.title.localeCompare(b.trip.title))
    .slice(0, 3);
};

export const getExperienceDNA = (trip) => {
  if (!trip) {
    return null;
  }

  return trip.experienceDNA || null;
};

const firstAppliesToEmployee = (first, employeeSlug) => (
  !employeeSlug
  || !first.employeeSlugs
  || first.employeeSlugs.includes(employeeSlug)
);

export const getPassportExperienceSummary = (trips = [], employeeSlug = '') => {
  const employeeTrips = trips.map((trip) => ({
    ...trip,
    firsts: (trip.firsts || []).filter((first) => firstAppliesToEmployee(first, employeeSlug)),
  }));

  return {
    tagStats: getTagCounts(trips),
    firsts: getFirstExperienceCounts(employeeTrips),
    achievements: trips.flatMap((trip) => trip.achievements || []),
  };
};
